import React, { FC, useState, useEffect, useRef } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { formatChartData, getChartOptions, generateLegend } from '../../../../services/widget-chart-service';
import { useTheme } from 'styled-components';

ChartJS.register(ArcElement, Tooltip, Legend, Title);

interface InstanceChartProps {
  instanceData: {
    instance_status_labels: string[];
    instance_status_data: number[];
    instance_region_labels: string[];
    instance_region_data: number[];
  };
}

const InstanceChart: FC<InstanceChartProps> = ({ instanceData }) => {
  const [activeTab, setActiveTab] = useState<'status' | 'regions'>('status');
  const [chartData, setChartData] = useState<any>({ labels: [], datasets: [] });
  
  const legendRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  const theme: any = useTheme();
  const palette = theme?.palette ?? {};
  const cardStyle: React.CSSProperties = {
    backgroundColor: palette[10] ?? '#ffffff',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: theme?.isDark ? '0 1px 3px rgba(15, 23, 42, 0.35)' : '0 1px 3px rgba(15, 23, 42, 0.08)',
    border: `1px solid ${palette[300] ?? '#e2e8f0'}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
  };
  const headingColor = palette[850] ?? '#0f172a';
  const tabContainerBackground = palette[20] ?? '#f9fafb';
  const emptyStateColor = palette[700] ?? '#6b7280';
  const emptyIconOpacity = theme?.isDark ? 0.5 : 0.3;

  const getCurrentData = () => {
    if (!instanceData) return { labels: [], data: [] };
    
    switch (activeTab) {
      case 'status':
        return {
          labels: instanceData.instance_status_labels,
          data: instanceData.instance_status_data
        };
      case 'regions':
        return {
          labels: instanceData.instance_region_labels,
          data: instanceData.instance_region_data
        };
      default:
        return { labels: [], data: [] };
    }
  };

  const updateChartData = () => {
    const { labels, data } = getCurrentData();
    const formattedData = formatChartData({ labels, data });
    setChartData(formattedData);
  };

  useEffect(() => {
    updateChartData();
  }, [activeTab, instanceData]);

  useEffect(() => {
    if (chartRef.current && legendRef.current) {
      legendRef.current.innerHTML = generateLegend(chartRef.current);
    }
  }, [chartData]);

  const chartOptions = getChartOptions(
    `Instance ${activeTab}`, 
    `instance-${activeTab}-doughnut-legend`
  );

  const getTabTitle = () => {
    switch (activeTab) {
      case 'status':
        return 'Status';
      case 'regions':
        return 'Regions';
      default:
        return 'Status';
    }
  };

  if (!instanceData) {
    return <p>No instance data available.</p>;
  }

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <h2 style={{
          fontSize: '16px',
          fontWeight: '600',
          color: headingColor,
          textAlign: 'center'
        }}>
          Instance Analytics
        </h2>
        
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '4px',
          borderRadius: '8px',
          padding: '4px',
          backgroundColor: tabContainerBackground
        }}>
          {(['status', 'regions'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '10px 20px',
                border: 'none',
                backgroundColor: activeTab === tab ? '#4671F6' : tabContainerBackground,
                color: activeTab === tab ? '#ffffff' : (palette[700] ?? '#64748b'),
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === tab ? '600' : '500',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                textTransform: 'capitalize',
                minWidth: '88px'
              }}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {chartData.labels.length > 0 ? (
        <div style={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <h3 style={{ 
            color: '#1d4ed8',
            fontSize: '14px',
            fontWeight: '600',
            textAlign: 'center'
          }}>
            {getTabTitle()}
          </h3>
          <div style={{ 
            maxWidth: '280px', 
            position: 'relative',
            height: '220px'
          }}>
            <Doughnut
              ref={chartRef}
              data={chartData}
              options={chartOptions}
            />
          </div>
          <div ref={legendRef} style={{ 
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '8px',
            width: '100%'
          }} />
        </div>
      ) : (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          color: emptyStateColor
        }}>
          <div>
            <div style={{
              fontSize: '48px',
              marginBottom: '16px',
              opacity: emptyIconOpacity
            }}>
              📊
            </div>
            <p>No data available for chart.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstanceChart;
